import AsyncStorage from "@react-native-async-storage/async-storage";

const STORAGE_KEY = "synapse_player_id";

function generateUUID(): string {
  // crypto.randomUUID where available, fallback for React Native
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback: RFC4122 v4 UUID
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/** Remove a player ID stored by earlier builds. Used while telemetry is off. */
export async function clearStoredPlayerId(): Promise<void> {
  await AsyncStorage.removeItem(STORAGE_KEY);
}

export class IdentityService {
  private playerId: string | null = null;
  private apiBase: string;

  constructor(apiBase: string) {
    this.apiBase = apiBase;
  }

  async getOrCreatePlayerId(): Promise<string> {
    // Return cached ID
    if (this.playerId) {
      return this.playerId;
    }

    // Try to recover from storage
    const stored = await AsyncStorage.getItem(STORAGE_KEY);
    if (stored) {
      this.playerId = stored;
      return stored;
    }

    // Generate new ID
    const id = generateUUID();
    this.playerId = id;

    await AsyncStorage.setItem(STORAGE_KEY, id);
    return id;
  }

  /** Register this player with the server. Only called once the player has
   *  consented to telemetry; creating an ID never contacts the server. */
  async register(): Promise<void> {
    const id = await this.getOrCreatePlayerId();
    const res = await fetch(`${this.apiBase}/api/v1/players`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id, consent_status: "pending" }),
    });

    if (res.status === 409) {
      // Already registered — not an error
      return;
    }

    if (!res.ok) {
      throw new Error(`Registration failed: ${res.status}`);
    }
  }

  async updateConsent(status: "opted_in" | "opted_out"): Promise<void> {
    const id = await this.getOrCreatePlayerId();

    try {
      await fetch(`${this.apiBase}/api/v1/players/${id}/consent`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ consent_status: status }),
      });
    } catch {
      // Consent update failure is not fatal — retry next time
    }
  }
}
